/**
 * Trash Flowers product page blocks (snippets/tf-product-extras.liquid):
 *   <tf-product-style>   rounded box + Add to cart button with arrow and live price
 *   <tf-product-upsell>  "+ Add" product cards
 *   <tf-product-addons>  on/off add-ons that go into the cart with the product
 *   <tf-product-share>   share icons
 *   <tf-product-tiers>   package cards that pick the matching variant
 *   <tf-product-custom-form>  order details sent with Add to cart (line item properties)
 *   <tf-hat-color-picker>     hat color chart in the order details (snippets/tf-hat-color-picker.liquid)
 *   product videos            looping clips with a sound button (snippets/product-thumbnail.liquid)
 */
(() => {
  if (window.TFProduct) return;

  const ARROW =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  const ADDONS_CHANGE = 'tf-product:addons-change';

  const scopeOf = (el) => el.closest('.product__info-container') || el.closest('section') || document;

  // Dawn has a hidden installments form before the real one; use the form with the Add to cart button.
  function mainFormIn(scope) {
    const forms = Array.from(scope.querySelectorAll('form[action*="/cart/add"]'));
    return (
      forms.find((form) => form.querySelector('[name="add"]')) ||
      forms.find((form) => form.querySelector('[type="submit"]')) ||
      null
    );
  }

  // Not form.id: Dawn's form has an <input name="id">, which shadows that property.
  function formIdOf(form) {
    if (!form) return '';
    if (!form.getAttribute('id')) form.setAttribute('id', `tf-product-form-${Math.random().toString(36).slice(2, 8)}`);
    return form.getAttribute('id');
  }

  const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Show the picked option's photo in an add-on / upsell card's thumbnail.
  function showOptionImage(card, option) {
    const img = card.querySelector('.tf-pe-img');
    if (!img || !option || !option.dataset.img) return;
    img.removeAttribute('srcset');
    img.src = option.dataset.img;
  }

  // Dawn's pub/sub (assets/pubsub.js). Returns an unsubscribe function, or null.
  function onThemeEvent(name, callback) {
    // eslint-disable-next-line no-undef
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS[name]) return null;
    // eslint-disable-next-line no-undef
    return subscribe(PUB_SUB_EVENTS[name], callback);
  }

  // "Swipe carousel" layout: arrows for mouse users, shown only when the cards overflow.
  function initCarousel(root) {
    const list = root.querySelector('.tf-pe-list');
    const nav = root.querySelector('[data-tf-pe-nav]');
    if (!list || !nav) return;
    const [prev, next] = nav.querySelectorAll('[data-tf-pe-scroll]');
    const update = () => {
      const max = list.scrollWidth - list.clientWidth;
      nav.hidden = max <= 1;
      prev.disabled = list.scrollLeft <= 1;
      next.disabled = list.scrollLeft >= max - 1;
    };
    nav.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-tf-pe-scroll]');
      if (!arrow) return;
      const card = list.querySelector('[data-tf-pe-card]');
      const step = card ? card.getBoundingClientRect().width + 16 : list.clientWidth;
      list.scrollBy({ left: step * Number(arrow.dataset.tfPeScroll), behavior: 'smooth' });
    });
    list.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
    update();
  }

  class TFProductStyle extends HTMLElement {
    connectedCallback() {
      this.scope = scopeOf(this);
      this.form = mainFormIn(this.scope);
      this.formId = formIdOf(this.form);
      this.button = this.form && (this.form.querySelector('[name="add"]') || this.form.querySelector('[type="submit"]'));
      try {
        this.variants = JSON.parse(this.querySelector('[data-tf-pe-variants]').textContent);
      } catch (_) {
        this.variants = {};
      }
      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });
      } catch (_) {
        this.money = { format: (n) => n.toFixed(2) };
      }

      if (this.dataset.box === 'true' && this.scope !== document) this.scope.classList.add('tf-pe-box');
      this.styleButton();

      this.onDocumentChange = (event) => {
        const name = event.target && event.target.name;
        if (name === 'id' || name === 'quantity') this.updatePrice();
      };
      this.onAddonsChange = () => this.updatePrice();
      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener(ADDONS_CHANGE, this.onAddonsChange);

      const hookTheme = () => {
        if (this.isConnected && !this.unsubscribe) this.unsubscribe = onThemeEvent('variantChange', () => this.updatePrice());
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hookTheme, { once: true });
      else hookTheme();

      this.updatePrice();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener(ADDONS_CHANGE, this.onAddonsChange);
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      if (this.scope && this.scope !== document) this.scope.classList.remove('tf-pe-box');
    }

    styleButton() {
      const button = this.button;
      if (!button || this.dataset.button !== 'true') return;
      button.classList.add('tf-atc');
      const label = button.querySelector(':scope > span');
      if (!label) return;
      // <i> elements, not <span>: Dawn rewrites the button's first <span> on variant change.
      if (this.dataset.arrow === 'true' && !button.querySelector('.tf-atc-icon')) {
        const icon = document.createElement('i');
        icon.className = 'tf-atc-icon';
        icon.innerHTML = ARROW;
        label.before(icon);
      }
      if (this.dataset.price === 'true') {
        this.price = button.querySelector('.tf-atc-price');
        if (!this.price) {
          this.price = document.createElement('i');
          this.price.className = 'tf-atc-price';
          label.after(this.price);
        }
      }
    }

    updatePrice() {
      if (!this.price || !this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      const unit = idInput ? this.variants[idInput.value] : undefined;
      if (unit === undefined) {
        this.price.textContent = '';
        return;
      }
      const qtyInput =
        (this.formId && document.querySelector(`[name="quantity"][form="${this.formId}"]`)) ||
        this.form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      const addons = Array.from(this.scope.querySelectorAll('[data-tf-pe-toggle]:checked')).reduce(
        (sum, toggle) => sum + (parseInt(toggle.dataset.price, 10) || 0),
        0
      );
      this.price.textContent = ` - ${this.money.format((unit * quantity + addons) / 100)}`;
    }
  }

  class TFProductAddons extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      // The add-on fields sit outside the product form; attach them to it so "Add to cart" sends them.
      const formId = formIdOf(mainFormIn(scopeOf(this)));
      if (formId) this.querySelectorAll('[data-tf-pe-field]').forEach((field) => field.setAttribute('form', formId));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      initCarousel(this);
    }

    // The "See the prints" pop-up: open, close (button or backdrop), and "Add" picks that style and turns the add-on on.
    onClick(event) {
      const target = event.target;
      const open = target.closest('[data-tf-pe-info-open]');
      if (open) {
        const dialog = open.closest('[data-tf-pe-card]').querySelector('[data-tf-pe-dialog]');
        if (dialog) dialog.showModal();
        return;
      }
      if (target.matches('[data-tf-pe-dialog]') || target.closest('[data-tf-pe-info-close]')) {
        target.closest('[data-tf-pe-dialog]').close();
        return;
      }
      const pick = target.closest('[data-tf-pe-pick]');
      if (!pick) return;
      const card = pick.closest('[data-tf-pe-card]');
      const select = card.querySelector('[data-tf-pe-variant]');
      if (select && select.value !== pick.dataset.tfPePick) {
        select.value = pick.dataset.tfPePick;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const toggle = card.querySelector('[data-tf-pe-toggle]');
      if (toggle && !toggle.checked) {
        toggle.checked = true;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
      pick.closest('[data-tf-pe-dialog]').close();
    }

    onChange(event) {
      const target = event.target;
      const card = target.closest('[data-tf-pe-card]');
      if (!card) return;
      if (target.matches('[data-tf-pe-toggle]')) {
        card.querySelectorAll('[data-tf-pe-field]').forEach((field) => {
          field.disabled = !target.checked;
        });
        card.classList.toggle('is-active', target.checked);
      } else if (target.matches('[data-tf-pe-variant]')) {
        const option = target.selectedOptions[0];
        const price = card.querySelector('[data-tf-pe-price]');
        if (price && option) price.textContent = option.dataset.price;
        const idField = card.querySelector('[data-tf-pe-field="id"]');
        if (idField) idField.value = target.value;
        const toggle = card.querySelector('[data-tf-pe-toggle]');
        if (toggle && option) toggle.dataset.price = option.dataset.cents;
        showOptionImage(card, option);
      } else {
        return;
      }
      document.dispatchEvent(new CustomEvent(ADDONS_CHANGE, { detail: { addons: this } }));
    }
  }

  class TFProductUpsell extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      initCarousel(this);
    }

    onChange(event) {
      const select = event.target;
      if (!select.matches('[data-tf-pe-variant]')) return;
      const card = select.closest('[data-tf-pe-card]');
      const option = select.selectedOptions[0];
      const price = card.querySelector('[data-tf-pe-price]');
      if (price && option) price.textContent = option.dataset.price;
      const add = card.querySelector('[data-tf-pe-add]');
      if (add) add.dataset.variantId = select.value;
      showOptionImage(card, option);
    }

    onClick(event) {
      const button = event.target.closest('[data-tf-pe-add]');
      if (!button || button.disabled) return;
      const card = button.closest('[data-tf-pe-card]');
      const error = card.querySelector('[data-tf-pe-error]');
      button.disabled = true;
      if (error) error.hidden = true;
      fetch(`${cartRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
      })
        .then((response) =>
          response.json().then((data) => {
            if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          })
        )
        .then(() => {
          const drawer = document.querySelector('tf-cart-drawer');
          if (!drawer) {
            window.location.href = (window.routes && window.routes.cart_url) || `${cartRoot()}cart`;
          } else if (!(drawer.config && drawer.config.syncApps)) {
            // The drawer refreshes and opens by itself when it sees a cart change; only do it here if that's off.
            drawer.refresh().then(() => drawer.open());
          }
        })
        .catch((err) => {
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
        })
        .finally(() => {
          button.disabled = false;
        });
    }
  }

  class TFProductShare extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.note = this.querySelector('[data-tf-share-note]');
      this.addEventListener('click', (event) => this.onClick(event));
    }

    // The product link, with the variant the shopper has picked.
    shareUrl() {
      const url = new URL(this.dataset.url, window.location.href);
      const variant = new URLSearchParams(window.location.search).get('variant');
      if (variant && window.location.pathname === url.pathname) url.searchParams.set('variant', variant);
      return url.toString();
    }

    onClick(event) {
      const link = event.target.closest('a.tf-share__btn');
      if (link) {
        // Keep the link working without JS, but share the variant picked since the page loaded.
        const href = new URL(link.href);
        ['url', 'u'].forEach((key) => {
          if (href.searchParams.has(key)) href.searchParams.set(key, this.shareUrl());
        });
        link.href = href.toString();
        return;
      }
      const app = event.target.closest('[data-tf-share-app]');
      if (app) {
        this.shareToApp(app.dataset.tfShareApp);
        return;
      }
      if (event.target.closest('[data-tf-share-copy]')) this.copy('Link copied');
    }

    // Instagram and TikTok have no web share link. On phones, the share menu lists them;
    // elsewhere, copy the link so it can be pasted into the app.
    shareToApp(app) {
      const url = this.shareUrl();
      const touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (touch && navigator.share) {
        navigator.share({ title: this.dataset.title, url }).catch((err) => {
          if (!err || err.name !== 'AbortError') this.copy(`Link copied. Paste it into ${app}.`);
        });
        return;
      }
      this.copy(`Link copied. Paste it into ${app}.`);
    }

    copy(message) {
      const url = this.shareUrl();
      const fallback = () => {
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(field);
        field.select();
        let copied = false;
        try {
          copied = document.execCommand('copy');
        } catch (_) {
          copied = false;
        }
        field.remove();
        // Couldn't copy: show the link so it can be copied by hand.
        this.showNote(copied ? message : url);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(() => this.showNote(message), fallback);
      } else {
        fallback();
      }
    }

    showNote(text) {
      if (!this.note) return;
      this.note.textContent = text;
      this.note.hidden = false;
      clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => {
        this.note.hidden = true;
      }, 5000);
    }
  }

  class TFProductTiers extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.scope = scopeOf(this);
      this.addEventListener('click', (event) => {
        const card = event.target.closest('.tf-tier');
        if (card && !card.disabled) this.select(card.dataset.value);
      });
      // Dawn re-renders the variant picker after a change, so read the selection back afterwards.
      this.onChange = () => window.setTimeout(() => this.sync(), 0);
      this.scope.addEventListener('change', this.onChange);
      const hookTheme = () => {
        if (this.isConnected && !this.unsubscribe) this.unsubscribe = onThemeEvent('variantChange', () => this.sync());
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hookTheme, { once: true });
      else hookTheme();
    }

    disconnectedCallback() {
      if (this.scope) this.scope.removeEventListener('change', this.onChange);
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      this.ready = false;
    }

    pickerInputs() {
      const name = this.dataset.option;
      const radios = Array.from(this.scope.querySelectorAll('variant-selects input[type="radio"]')).filter((input) => input.name === name);
      const select = Array.from(this.scope.querySelectorAll('variant-selects select')).find((el) => el.name === `options[${name}]`);
      return { radios, select };
    }

    select(value) {
      const { radios, select } = this.pickerInputs();
      const radio = radios.find((input) => input.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.mark(value);
    }

    sync() {
      const { radios, select } = this.pickerInputs();
      const checked = radios.find((input) => input.checked);
      const value = checked ? checked.value : select && select.value;
      if (value) this.mark(value);
    }

    mark(value) {
      this.querySelectorAll('.tf-tier').forEach((card) => {
        const on = card.dataset.value === value;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-checked', String(on));
      });
    }
  }

  class TFProductCustomForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = mainFormIn(scopeOf(this));
      const formId = formIdOf(this.form);
      this.fields = Array.from(this.querySelectorAll('[data-tf-form-field]'));
      // The fields sit outside the product form; attach them so "Add to cart" sends them with the hat.
      if (formId) this.fields.forEach((field) => field.setAttribute('form', formId));
      // Dawn's product form has novalidate, so check required fields here, before the cart drawer or Dawn send it.
      this.onSubmit = (event) => {
        if (!this.form || event.target !== this.form) return;
        const invalid = this.fields.find((field) => !field.checkValidity());
        if (!invalid) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const wrapper = invalid.closest('.tf-field');
        if (wrapper) wrapper.classList.add('is-invalid');
        invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        invalid.focus({ preventScroll: true });
        invalid.reportValidity();
      };
      window.addEventListener('submit', this.onSubmit, true);
      this.addEventListener('input', (event) => {
        const wrapper = event.target.closest('.tf-field');
        if (wrapper) wrapper.classList.remove('is-invalid');
      });
    }

    disconnectedCallback() {
      window.removeEventListener('submit', this.onSubmit, true);
      this.ready = false;
    }
  }

  // The hat color chart: a button opens it, picking a hat fills the hidden "Hat color & style" field (sent with the
  // order by <tf-product-custom-form>) and shows the pick on the button.
  class TFHatColorPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('[data-tf-hatpick-value]');
      this.current = this.querySelector('[data-tf-hatpick-current]');
      this.opener = this.querySelector('[data-tf-hatpick-open]');
      if (!this.dialog || !this.input || !this.opener) return;
      this.opener.addEventListener('click', () => this.open());
      this.dialog.addEventListener('click', (event) => {
        // A click on the dialog itself is a click on the dimmed backdrop around the chart.
        if (event.target === this.dialog || event.target.closest('[data-tf-hatpick-close]')) {
          this.dialog.close();
          return;
        }
        const option = event.target.closest('[data-value]');
        if (option) this.pick(option);
      });
    }

    open() {
      this.dialog.showModal();
      const picked = this.dialog.querySelector('[data-value][aria-pressed="true"]');
      if (picked) picked.scrollIntoView({ block: 'center' });
    }

    pick(option) {
      this.dialog.querySelectorAll('[data-value]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === option));
      });
      this.input.value = option.dataset.value;
      const art = option.querySelector('.tf-hatpick__art');
      const text = document.createElement('span');
      text.className = 'tf-hatpick__picked';
      const name = document.createElement('strong');
      name.textContent = option.dataset.name;
      text.append(name);
      if (option.dataset.style) {
        const style = document.createElement('span');
        style.textContent = option.dataset.style;
        text.append(style);
      }
      this.current.replaceChildren(...(art ? [art.cloneNode(true)] : []), text);
      this.dialog.close();
      this.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  // Product videos play like a looping clip (snippets/product-thumbnail.liquid and product-media.liquid): muted, with
  // no browser controls. Tapping the clip pauses or plays it; the sound button turns the music on and off.
  document.addEventListener('click', (event) => {
    const media = event.target.closest('deferred-media[loaded]');
    const video = media && media.querySelector('video.tf-clip');
    if (!video) return;
    const sound = event.target.closest('.tf-clip-sound');
    if (sound) {
      video.muted = !video.muted;
      if (!video.muted && video.paused) video.play();
      sound.setAttribute('aria-pressed', String(!video.muted));
      sound.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
    } else if (event.target === video) {
      if (video.paused) video.play();
      else video.pause();
    }
  });
  // play and pause don't bubble, so listen while they travel down: shows the play sign over a paused clip.
  ['play', 'pause'].forEach((type) => {
    document.addEventListener(type, (event) => {
      const media = event.target.closest && event.target.closest('deferred-media');
      if (media && event.target.matches('video.tf-clip')) media.classList.toggle('tf-clip-paused', event.target.paused);
    }, true);
  });

  window.TFProduct = { mainFormIn, scopeOf };
  customElements.define('tf-product-style', TFProductStyle);
  customElements.define('tf-product-addons', TFProductAddons);
  customElements.define('tf-product-upsell', TFProductUpsell);
  customElements.define('tf-product-share', TFProductShare);
  customElements.define('tf-product-tiers', TFProductTiers);
  customElements.define('tf-product-custom-form', TFProductCustomForm);
  customElements.define('tf-hat-color-picker', TFHatColorPicker);
})();
